import React, {CSSProperties, useState} from 'react'

import ControlLabel from './ControlLabel.js'
import {FieldProps} from './types.js'
import {choiceTokens, getChoiceBoxColors, getChoiceOverlayStyle} from './choiceStyles.js'
import {markFieldRowControl, useFieldRowOffset} from './FormRowContext.js'
import isTouchDevice from 'src/utils/IsTouchDevice.js'

// shiftKey is only set when clicked, not when toggled with the space bar
export interface CheckChangeEvent {
    target: {checked: boolean}
    shiftKey?: boolean
}

// aria-label names the box for screen readers when there is no label
export interface CheckProps extends FieldProps, Pick<React.AriaAttributes, 'aria-label'> {
    checked?: boolean
    onChange?: (event: CheckChangeEvent) => void
    indeterminate?: boolean
}

const Check = ({disabled, readOnly, checked, onChange, label, info, style = {}, indeterminate = false, 'aria-label': ariaLabel}: CheckProps) => {
    const [focused, setFocused] = useState(false)
    const [hover, setHover] = useState<boolean>()
    const rowOffset = useFieldRowOffset(choiceTokens.height)

    // Read only keeps the normal look but ignores clicks and keys, like disabled
    const interactive = !disabled && !readOnly

    // Clicking focuses the box too, but the focus highlight is only shown again once a key is pressed
    const onClick = (event: React.MouseEvent<HTMLDivElement>) => {
        if (interactive && typeof onChange === 'function') {
            onChange({target: {checked: !checked}, shiftKey: event.shiftKey})
            setFocused(false)
            setHover(isTouchDevice ? false : hover)
        }
    }

    // Clicking the label toggles like clicking the box, unless an interactive element inside the label was clicked
    const onClickLabel = (event: React.MouseEvent<HTMLDivElement>) => {
        const target = event.target as HTMLElement

        if (target.closest && target.closest('a, button, input, select, textarea')) {
            return
        }

        if (interactive) {
            onChange?.({target: {checked: !checked}, shiftKey: event.shiftKey})
        }
    }

    // preventDefault stops the page from scrolling
    const onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        setFocused(true)

        if (event.key === ' ' || event.key === 'Spacebar' || event.keyCode === 32) {
            event.preventDefault()

            if (interactive) {
                onChange?.({target: {checked: !checked}})
            }
        }
    }

    const overLayStyle = getChoiceOverlayStyle({highlighted: hover || focused, interactive})

    const boxColors = getChoiceBoxColors({checked: checked || indeterminate, disabled})

    const checkboxStyle: CSSProperties = {
        display: 'flex',
        flexShrink: 0,
        justifyContent: 'center',
        alignItems: 'center',
        paddingBottom: 1,
        background: boxColors.background,
        width: 18,
        height: 18,
        borderRadius: 2,
        border: `1px solid ${boxColors.border}`,
        fontSize: checked || indeterminate ? 16 : 0,
        color: boxColors.foreground,
        transition: 'font-size .1s ease',
        outline: 'none'
    }

    const labelStyle: CSSProperties = {
        marginRight: choiceTokens.labelGap.check,
        cursor: interactive && typeof onChange === 'function' ? 'pointer' : undefined
    }

    const defaultStyle: CSSProperties = {
        display: 'flex',
        alignItems: 'center',
        flex: label && !style.width ? 1 : '',
        height: choiceTokens.height,
        ...choiceTokens.margins.check,
        ...rowOffset,
        ...style
    }

    return (
        <div style={defaultStyle}>
            <div
                style={overLayStyle}
                onClick={onClick}
                onMouseEnter={() => setHover(true)}
                onMouseLeave={() => setHover(false)}
            >
                <div
                    tabIndex={disabled ? -1 : 0}
                    role='checkbox'
                    aria-checked={indeterminate && !checked ? 'mixed' : !!checked}
                    aria-label={ariaLabel}
                    aria-disabled={disabled || undefined}
                    aria-readonly={readOnly || undefined}
                    style={checkboxStyle}
                    onKeyDown={onKeyDown}
                    onFocus={() => setFocused(true)}
                    onBlur={() => setFocused(false)}
                >
                    {checked ? <i className='mdi mdi-check' /> : indeterminate ? <i className='mdi mdi-minus' /> : ''}
                </div>
            </div>

            {label &&
                <ControlLabel style={labelStyle} info={info} disabled={disabled} onClick={onClickLabel}>
                    {label}
                </ControlLabel>
            }
        </div>
    )
}

// Was a PureComponent, memo keeps the shallow props comparison
export default markFieldRowControl(React.memo(Check))
